/* ----------------------------------
 * Shared base types
 * ---------------------------------- */

type Nullable<T> = T | null;

interface BasicInfo {
  id?: string | number;
  orgID?: string | number;
  round?: number;
  status?: string;
  text?: string;
}

interface InterviewDetails {
  startTime?: string;
  location?: string;
  duration?: number;
  interviewer?: string;
  interviewCoordinator?: string;
  isUpdateStartTime?: boolean;
  recordedVideoLink?: string;
}

interface OtherDetails {
  eventId?: string | number;
  createdAt?: string;
  updatedAt?: string;
  versionKey?: number;
  latestID?: string | number;
  message?: string;
  customLink?: string;
  subject?: string;
  timeZone?: string;
}

interface MeetingRoom {
  ID?: string | number;
  name?: string;
}

interface RawInterviewItem {
  basicInfo?: BasicInfo;
  interviewDetails?: InterviewDetails;
  otherDetails?: OtherDetails;
  MeetingRoom?: MeetingRoom;
  application?: any;
  applicationDetails?: any;
  applicationDetail?: any;
  // allow backend to send extra fields
  [key: string]: any;
}

interface ApiMeta {
  total?: number;
  [key: string]: any;
}

export interface InterviewApiResponse {
  data?: RawInterviewItem[] | RawInterviewItem;
  meta?: ApiMeta;
  [key: string]: any;
}

/* ----------------------------------
 * Normalized Interview type
 * ---------------------------------- */

export interface CalendarAttachment {
  fileId?: string;
  fileURL?: string;
  fileUrl?: string;
  mimeType: string;
  iconLink?: string;
  title: string;
}

export interface Interview {
  id: Nullable<string | number>;
  orgId: Nullable<string | number>;
  round: Nullable<number>;
  status: Nullable<string>;
  text: Nullable<string>;
  startTime: Nullable<string>;
  location: Nullable<string>;
  duration: Nullable<number>;
  interviewer: Nullable<string>;
  interviewCoordinator: Nullable<string>;
  isUpdateStartTime: Nullable<boolean>;
  recordedVideoLink: Nullable<string>;
  eventId: Nullable<string | number>;
  createdAt: Nullable<string>;
  updatedAt: Nullable<string>;
  versionKey: Nullable<number>;
  latestID: Nullable<string | number>;
  message: Nullable<string>;
  customLink: Nullable<string>;
  subject: Nullable<string>;
  timeZone: Nullable<string>;
  meetingRoomID: Nullable<string | number>;
  meetingRoomName: Nullable<string>;
  calendarEventAttachments: Nullable<CalendarAttachment[]>;
  application: Nullable<any>;
  // keep original fields
  [key: string]: any;
}

const normalizeInterviewApplication = (item: RawInterviewItem): Nullable<any> => {
  const applicationDetails = item?.applicationDetails ?? item?.applicationDetail;
  const application = item?.application;

  if (!applicationDetails && !application) return null;

  const detailsBasicInfo = applicationDetails?.basicInfo ?? {};
  const applicationBasicInfo = application?.basicInfo ?? {};
  const basicInfo = {
    ...detailsBasicInfo,
    ...applicationBasicInfo,
  };
  const normalizedApplication = {
    ...(applicationDetails ?? {}),
    ...(application ?? {}),
  };

  return {
    ...normalizedApplication,
    id: normalizedApplication.id ?? basicInfo.id ?? null,
    name: normalizedApplication.name ?? basicInfo.name ?? null,
    status: normalizedApplication.status ?? basicInfo.status ?? null,
    updatedStatus: normalizedApplication.updatedStatus ?? basicInfo.updatedStatus ?? null,
    basicInfo: Object.keys(basicInfo).length ? basicInfo : normalizedApplication.basicInfo,
  };
};

/* ----------------------------------
 * getAllInterviewMap
 * ---------------------------------- */

export function getAllInterviewMap(apiResponse?: InterviewApiResponse): {
  data: Interview[];
  meta: ApiMeta;
  interviewerName?: string;
} {
  if (!apiResponse) {
    return { data: [], meta: { total: 0 } };
  }

  if (apiResponse?.meta?.total === 0) {
    return {
      data: [],
      meta: { total: 0 },
      interviewerName: (apiResponse as any)?.data?.name,
    };
  }

  const data = (apiResponse?.data || apiResponse) as RawInterviewItem[];

  return {
    meta: {
      ...(apiResponse?.meta || {}),
    },
    data: data.map(
      (item): Interview => ({
        ...item,
        id: item?.basicInfo?.id ?? null,
        orgId: item?.basicInfo?.orgID ?? null,
        round: item?.basicInfo?.round ?? null,
        status: item?.basicInfo?.status ?? null,
        text: item?.basicInfo?.text ?? null,
        startTime: item?.interviewDetails?.startTime ?? null,
        location: item?.interviewDetails?.location ?? null,
        duration: item?.interviewDetails?.duration ?? null,
        interviewer: item?.interviewDetails?.interviewer ?? null,
        interviewCoordinator: item?.interviewDetails?.interviewCoordinator ?? null,
        isUpdateStartTime: item?.interviewDetails?.isUpdateStartTime ?? null,
        recordedVideoLink: item?.interviewDetails?.recordedVideoLink ?? null,
        eventId: item?.otherDetails?.eventId ?? null,
        createdAt: item?.otherDetails?.createdAt ?? null,
        updatedAt: item?.otherDetails?.updatedAt ?? null,
        versionKey: item?.otherDetails?.versionKey ?? null,
        latestID: item?.otherDetails?.latestID ?? null,
        message: item?.otherDetails?.message ?? null,
        customLink: item?.otherDetails?.customLink ?? null,
        subject: item?.otherDetails?.subject ?? null,
        timeZone: item?.otherDetails?.timeZone ?? null,
        meetingRoomID: item?.MeetingRoom?.ID ?? null,
        meetingRoomName: item?.MeetingRoom?.name ?? null,
        application: normalizeInterviewApplication(item),
      })
    ),
  };
}

/* ----------------------------------
 * getInterviewByIdMap
 * ---------------------------------- */

export function getInterviewByIdMap(apiResponse?: InterviewApiResponse): Interview | undefined {
  if (!apiResponse) return apiResponse as undefined;

  const data = (apiResponse?.data || apiResponse) as RawInterviewItem;

  return {
    ...data,
    id: data?.basicInfo?.id ?? null,
    orgId: data?.basicInfo?.orgID ?? null,
    round: data?.basicInfo?.round ?? null,
    status: data?.basicInfo?.status ?? null,
    text: data?.basicInfo?.text ?? null,
    startTime: data?.interviewDetails?.startTime ?? null,
    location: data?.interviewDetails?.location ?? null,
    duration: data?.interviewDetails?.duration ?? null,
    interviewer: data?.interviewDetails?.interviewer ?? null,
    interviewCoordinator: data?.interviewDetails?.interviewCoordinator ?? null,
    isUpdateStartTime: data?.interviewDetails?.isUpdateStartTime ?? null,
    recordedVideoLink: data?.interviewDetails?.recordedVideoLink ?? null,
    eventId: data?.otherDetails?.eventId ?? null,
    createdAt: data?.otherDetails?.createdAt ?? null,
    updatedAt: data?.otherDetails?.updatedAt ?? null,
    versionKey: data?.otherDetails?.versionKey ?? null,
    latestID: data?.otherDetails?.latestID ?? null,
    message: data?.otherDetails?.message ?? null,
    customLink: data?.otherDetails?.customLink ?? null,
    subject: data?.otherDetails?.subject ?? null,
    timeZone: data?.otherDetails?.timeZone ?? null,
    meetingRoomID: data?.MeetingRoom?.ID ?? null,
    meetingRoomName: data?.MeetingRoom?.name ?? null,
    calendarEventAttachments: data?.interviewDetails?.calendarEventAttachments ?? null,
    application: normalizeInterviewApplication(data),
  };
}
